// Libs
import classNames from 'classnames/bind';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Typography } from '@mui/material';
// Components, Layouts, Pages
import { ColumnCheckList, FormChecklistModal } from '~/components/module/checklist';
import { BaseToolbar, Loading } from '~/components/common';
// Others
import { getChecklist } from '~/thunks/checklist/checklistThunk';
import { checklistActions } from '~/thunks/checklist/checklistSlice';
import { ChecklistTypeEnum } from '~/utils/enum/common';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { IChecklist, IQueryParamChecklist } from '~/utils/interface/checklist';
import { usePermissions } from '~/utils/hooks/userPermissions';
import { CHECKLIST_PERMISSIONS } from '~/utils/constants/permissions';
// Styles, images, icons
import styles from './Checklist.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const Checklist = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/checklist', 'common/base']);
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  const { isRefreshChecklistList } = useAppSelector((state) => state.checklist);

  const { canCreateChecklists, canViewChecklists, canDeleteChecklists } = usePermissions(CHECKLIST_PERMISSIONS);
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [paramObject] = useState<IQueryParamChecklist>({
    type: ChecklistTypeEnum.DEFAULT,
  });

  const [dataChecklist, setDataChecklist] = useState<IChecklist[]>([]);

  const [isOpenChecklistFormModal, setIsOpenChecklistFormModal] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListChecklist(paramObject);
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshChecklistList) return;

    handleGetListChecklist(paramObject);

    dispatch(checklistActions.setRefreshChecklistList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshChecklistList]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListChecklist = (params: IQueryParamChecklist) => {
    setIsLoading(true);

    dispatch(getChecklist(params))
      .unwrap()
      .then((res) => {
        const { data } = res;
        if (!data) return;

        setDataChecklist(data || []);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleAddColumnChecklist = () => {
    handleShowChecklistFormModal();
  };

  const handleShowChecklistFormModal = () => {
    setIsOpenChecklistFormModal((prev) => !prev);
  };
  //#endregion Handle Function

  return (
    <Box id='checklistPage' className={cx('container')}>
      <BaseToolbar title={t('checklist_title')} isBack description={t('checklist_sub_title')}>
        {canCreateChecklists && (
          <Box className={cx('button')}>
            <Button
              type='button'
              variant='contained'
              className={cx('buttonAction')}
              onClick={handleAddColumnChecklist}
              startIcon={<img src={icons.commonIconPlus} alt={'common_alt_img'} />}
            >
              {t('checklist_title_button_add')}
            </Button>
          </Box>
        )}
      </BaseToolbar>

      <Box className={cx('content')}>
        <Box component={'div'} className={cx('body')}>
          {canViewChecklists && dataChecklist && dataChecklist.length > 0 ? (
            dataChecklist.map((item, index) => {
              return (
                <ColumnCheckList
                  checkListData={item}
                  key={index}
                  isDelete={canDeleteChecklists}
                  isDeleteItem={canDeleteChecklists}
                />
              );
            })
          ) : (
            <Typography align='center' className={cx('noData')}>
              {t('common_no_data_available', { ns: 'common/base' })}
            </Typography>
          )}
        </Box>
      </Box>

      {isOpenChecklistFormModal && <FormChecklistModal onClose={handleShowChecklistFormModal} />}

      {isLoading && <Loading />}
    </Box>
  );
};

export default Checklist;
